import type {MultiValue} from 'react-select';
import ReactSelect from 'react-select';

type Option = {
  value: string;
  label: string;
  key?: string;
};
type SelectProps = {
  id: string;
  name: string;
  dataCy?: string;
  options: Option[];
  onChange: (value: string) => void;
  value: string;
  className?: string;
  placeholder?: string;
};

const mapOptionsToString = (options: Option[] = []): string =>
  options
    ?.filter(Boolean)
    ?.map((item) => item?.value || '')
    ?.filter(Boolean)
    .join(',');

const mapStringToOptions = (options: string = ''): Option[] =>
  options
    ?.trim()
    ?.split(',')
    ?.filter(Boolean)
    ?.map((item) => ({
      value: item?.trim(),
      key: item?.trim(),
      label: item?.trim(),
    }));
export const MultiSelect = ({
  options,
  onChange,
  value,
  id,
  name,
  dataCy,
  className,
  placeholder,
}: SelectProps) => {
  const select = (
    <ReactSelect
      isMulti
      options={options}
      onChange={(val: MultiValue<Option>) => {
        const value = mapOptionsToString(val as Option[]);
        onChange(value);
      }}
      value={mapStringToOptions(value)}
      className={className}
      id={id}
      name={name}
      placeholder={placeholder || '---Select One---'}
      // Dark-mode styling (light mode keeps react-select defaults). `important: true` in
      // the Tailwind config lets these classes override react-select's emotion styles.
      classNames={{
        control: () => 'dark:!bg-transparent dark:!border-dark-borderHeavy',
        menu: () =>
          'dark:!bg-dark-inputBg dark:!border dark:!border-dark-borderHeavy',
        option: (state) => {
          if (state.isSelected) {
            return 'dark:!bg-gray-600 dark:!text-white';
          }
          if (state.isFocused) {
            return 'dark:!bg-gray-700 dark:!text-white';
          }
          return 'dark:!bg-dark-inputBg dark:!text-gray-100';
        },
        multiValue: () => 'dark:!bg-gray-700',
        multiValueLabel: () => 'dark:!text-white',
        multiValueRemove: () => 'dark:!text-gray-300 dark:hover:!bg-gray-600',
        input: () => 'dark:!text-white',
        singleValue: () => 'dark:!text-white',
        placeholder: () => 'dark:!text-gray-400',
      }}
    />
  );

  return dataCy ? (
    <div data-cy={`${dataCy}-dropdown-input`}>{select}</div>
  ) : (
    select
  );
};
